import React from 'react';
import { useRouter } from 'next/router';
import PropTypes, { InferProps } from 'prop-types';
import { useIntl } from 'react-intl';
import ProfileContent, { BreadcrumbsWrapper }
  from '@fuww/library/src/CompanyProfile';
import ImageContainer
  from '@fuww/library/src/CompanyProfile/ImageContainer';
import SocialShare from '@fuww/library/src/SocialShare';
import { ImageItem } from '@fuww/library/src/ImageGrid';
import EmbedVideo from '@fuww/library/src/EmbedVideo';
import Button from '@fuww/library/src/Button';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import { Breadcrumbs } from '@fuww/library/src/Breadcrumb';
import { useSiteContext } from '../SiteContext';
import Head from '../Head';
import Image from '../Image';
import CompanyAbout from './CompanyProfilePages/About';
import shareList from '../../lib/shareData';
import profileTabsPropertyTypes from './profileTabsPropertyTypes';
import withQuery from '../../lib/withQuery';
import profileBySlugQuery
  from '../../lib/queries/products/profileBySlugQuery.graphql';
import messages from '../../lib/messages.json';
import Routes from '../../routes.mjs';
import LinkAndAnchor from '../LinkAndAnchor';
import HomeLink from '../HomeLink';
import {
  companyProfileCollectionImageConfigurations,
  companyProfileImageConfigurations,
  companyProfileLogoImageConfigurations,
  companyProfileMobileImageConfigurations,
} from '../../lib/imageConfigurations';
import throw404 from '../../lib/throw404';

const profilePropertyTypes = {
  data: PropTypes.shape({
    profileBySlug: PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['COMPANY', 'SCHOOL'] as const).isRequired,
      page: PropTypes.shape({
        premium: PropTypes.bool,
        slug: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        b2bPartnerPolicy: PropTypes.string,
        about: PropTypes.string,
        contactDetails: PropTypes.string,
        videoUrl: PropTypes.string,
        industries: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        responsiveCollectionImageUrls: PropTypes.arrayOf(
          PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
        ),
        imageUrls: PropTypes.arrayOf(PropTypes.string).isRequired,
      }).isRequired,
      company: PropTypes.shape({
        url: PropTypes.string,
        description: PropTypes.string,
      }).isRequired,
      canUpdate: PropTypes.bool,
      ...profileTabsPropertyTypes,
    }),
  }).isRequired,
};

type ProfileProperties = InferProps<typeof profilePropertyTypes>;

const Profile = ({ data }: ProfileProperties) => {
  const { route: currentRouteWithSlash, query } = useRouter();
  const intl = useIntl();
  const { origin, locale } = useSiteContext();

  if (!data.profileBySlug) return throw404();

  const { profileBySlug } = data;

  const currentRoute = currentRouteWithSlash.slice(1);
  const { slug } = query;

  const {
    id,
    type,
    page: {
      premium,
      title,
      b2bPartnerPolicy,
      about,
      contactDetails,
      videoUrl,
      industries,
      responsiveCollectionImageUrls,
      imageUrls,
    },
    company: {
      url,
      description,
    },
    canUpdate,
  } = profileBySlug;

  const metaImage = imageUrls[7];
  const socialShareUrl = Routes.findAndGetUrls(
    currentRoute,
    query,
  ).urls.as;

  const profileUrl = `${origin}${socialShareUrl}`;

  const filteredShareList = shareList({
    title,
    media: metaImage ?? undefined,
    url: profileUrl,
    locale,
    page: 'profile',
  }).filter(
    (share: { checkMedia?: boolean }) => !share.checkMedia || metaImage,
  );

  const breadcrumbs = [
    <HomeLink />,
    <LinkAndAnchor
      route="company-directory"
      passHref
    >
      {intl.formatMessage(messages.companies)}
    </LinkAndAnchor>,
    <LinkAndAnchor
      route="company-profile"
      passHref
      params={{ slug }}
    >
      {title}
    </LinkAndAnchor>,
  ];

  return (
    <>
      <Head
        imageUrl={metaImage}
        title={
          type === 'COMPANY'
            ? intl.formatMessage(messages['company-profile.title'], { title })
            : title
        }
        description={
          type === 'COMPANY'
            ? intl.formatMessage(
              messages['company-profile.description'], { title },
            )
            : title
        }
        subjectType="Profile"
        subjectId={id}
      />
      <BreadcrumbsWrapper isProfilePage>
        <Breadcrumbs
          crumbs={breadcrumbs}
        />
      </BreadcrumbsWrapper>
      <CompanyAbout
        profile={profileBySlug}
      >
        <ProfileContent
          socialShare={<SocialShare data={filteredShareList} />}
          about={about}
          description={description}
          website={url}
          contactDetails={contactDetails}
          video={videoUrl && (
            <EmbedVideo
              title={`Video ${title}`}
              src={videoUrl}
            />
          )}
          policy={b2bPartnerPolicy}
          industry={industries.map((industry) => (
            <span key={industry}>
              {industry}
            </span>
          ))}
          imageCollection={
            responsiveCollectionImageUrls?.map((imageUrl) => (
              <ImageItem key={imageUrl[0]}>
                <ImageContainer>
                  <Image
                    imageClassName="image-collection"
                    src={imageUrl[1]}
                    alt={`Collection image ${title}`}
                    height="350px"
                    width="100%"
                    objectFit="cover"
                    sizes={responsiveCollectionImageUrls.length === 1
                      ? '(min-width: 623px) 575px, 93vw'
                      : `(min-width: ${breakpoints.sm}px) 280px, 92vw`}
                    placeholder={imageUrl[0]}
                    srcSet={`${imageUrl[1]
                    } 280w, ${imageUrl[2]
                    } 560w, ${imageUrl[3]
                    } 936w`}
                  />
                </ImageContainer>
              </ImageItem>
            ))
          }
          editButton={premium && canUpdate && (
            <LinkAndAnchor
              route="company-profile-edit"
              params={{ slug }}
            >
              <Button>
                {intl.formatMessage(messages['company-profile.editProfile'])}
              </Button>
            </LinkAndAnchor>
          )}
        />
      </CompanyAbout>
    </>
  );
};

Profile.propTypes = profilePropertyTypes;

const ProfileWithQuery = withQuery(Profile);

const profilePagePropertyTypes = {
  slug: PropTypes.string.isRequired,
};

type ProfilePageProperties = InferProps<typeof profilePagePropertyTypes>;

const ProfilePage = ({ slug }: ProfilePageProperties) => (
  <ProfileWithQuery
    query={profileBySlugQuery}
    variables={{
      slug,
      imageConfigurations: companyProfileImageConfigurations,
      mobileImageConfigurations: companyProfileMobileImageConfigurations,
      logoConfigurations: companyProfileLogoImageConfigurations,
      collectionImageConfigurations:
      companyProfileCollectionImageConfigurations,
    }}
    errorMessage="Error loading this company profile"
  />
);

ProfilePage.propTypes = profilePagePropertyTypes;

export default ProfilePage;
